/**
 * Betreuungsmengen, wie eine Kita sie ausspricht.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum Brüche und nicht Dezimalzahlen
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil niemand in einer Kita «zwei Punkt fünf Ganztage» sagt. Gesprochen wird
 * von zweieinhalb Tagen, und auf dem Anmeldeformular steht ein halber Tag und
 * kein 0.5. Eine Elternrechnung, die «2.5 Ganztage» ausweist, zwingt die Mutter
 * zum Umrechnen, bevor sie prüfen kann, ob es stimmt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was hier NICHT passiert
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Gerechnet wird weiter mit 2.5. Diese Datei formatiert, sie rechnet nicht:
 * Die Menge in `document_lines.quantity` bleibt die exakte Dezimalzahl, die
 * Tarifrechnung bleibt unberührt, und die Summen kommen wie bisher aus
 * `calcBeleg()`.
 *
 * Und sie gilt ausschliesslich für die Kinderbetreuung. Kein Beleg einer
 * anderen Branche läuft hier durch – ein Parkettleger, der 12.375 m² verlegt,
 * will keine Brüche sehen, und 12⅜ wäre auf seiner Rechnung schlicht falsch
 * gelesen. Deshalb eine eigene Datei mit einem eigenen Namen statt einer
 * Erweiterung von `money.ts`: Wer sie aufruft, tut es absichtlich.
 */

/** Die Viertel, die eine Kita überhaupt vergibt. */
const VIERTEL: Record<number, string> = {
  0: '',
  1: '¼',
  2: '½',
  3: '¾'
}

/**
 * Eine Menge als natürlicher Bruch – «2½» statt «2.5».
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was mit einer Zahl passiert, die kein Viertel ist
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Sie bleibt stehen, wie sie ist. 1.1 wird zu «1.1» und nicht zu «1⅒» oder
 * gerundet zu «1». Das ist die wichtigere Hälfte dieser Funktion: Eine
 * Darstellung, die eine ungewöhnliche Menge auf den nächsten Bruch zieht,
 * zeigt eine andere Zahl als die, mit der gerechnet wird – und auf einer
 * Rechnung ist das keine Unschönheit, sondern ein falscher Betrag im Text
 * neben dem richtigen in der Spalte.
 *
 * Deshalb wird nur umgeschrieben, was auf dem Viertel aufgeht; alles andere
 * geht als Dezimalzahl hinaus, in Schweizer Schreibweise ohne Tausendertrenner
 * (eine Betreuungsmenge erreicht keine Tausend).
 */
export function alsBruch(menge: number): string {
  if (!Number.isFinite(menge)) return ''
  const negativ = menge < 0
  const betrag = Math.abs(menge)

  /* Auf dem Viertel? Dann der Bruch. Sonst die Zahl, unverändert. */
  const viertel = betrag * 4
  if (Math.abs(viertel - Math.round(viertel)) > 1e-9) {
    return `${negativ ? '-' : ''}${String(betrag).replace(/\.0+$/, '')}`
  }

  const ganze = Math.floor(betrag)
  const rest = Math.round((betrag - ganze) * 4)
  const bruch = VIERTEL[rest] ?? ''
  const text =
    bruch === ''
      ? String(ganze)
      : ganze === 0
        ? bruch
        : `${ganze}${bruch}`
  return `${negativ ? '-' : ''}${text}`
}

/** Die drei Betreuungsarten, die als Monatspauschale laufen. */
export type Betreuungsart = 'ganztag' | 'halbtag_ohne' | 'halbtag_mit'

/**
 * Wie die Art heisst, wenn eine Menge davorsteht.
 *
 * Mehrzahl und Einzahl getrennt, weil «1 ganze Tage» niemand schreibt und
 * eine Rechnung, die so aussieht, als hätte sie niemand gelesen, auch sonst
 * nicht geprüft wirkt.
 *
 * Ein halber Tag bleibt dabei in der Mehrzahl: «½ ganze Tage pro Woche» ist
 * die Menge einer Woche und nicht ein Tag – die Einzahl gilt nur für genau
 * eins.
 */
const ARTTEXT: Record<Betreuungsart, { eins: string; viele: string }> = {
  ganztag: { eins: 'ganzer Tag pro Woche', viele: 'ganze Tage pro Woche' },
  halbtag_ohne: {
    eins: 'halber Tag ohne Mittagessen pro Woche',
    viele: 'halbe Tage ohne Mittagessen pro Woche'
  },
  halbtag_mit: {
    eins: 'halber Tag mit Mittagessen pro Woche',
    viele: 'halbe Tage mit Mittagessen pro Woche'
  }
}

/** Nur die Art, ohne Menge – für Überschriften und Auswahllisten. */
export const BETREUUNGSART_LABEL: Record<Betreuungsart, string> = {
  ganztag: 'Ganze Tage',
  halbtag_ohne: 'Halbe Tage ohne Mittagessen',
  halbtag_mit: 'Halbe Tage mit Mittagessen'
}

/** «2½ ganze Tage pro Woche» – Menge und Art in einem Satzteil. */
export function mengentext(art: Betreuungsart, menge: number): string {
  const wort = ARTTEXT[art]
  return `${alsBruch(menge)} ${menge === 1 ? wort.eins : wort.viele}`
}

/**
 * Der Zusatz, der auf der Rechnungsposition hinter dem Leistungsnamen steht.
 *
 * Er steht IN DER BESCHREIBUNG und nicht in der Mengenspalte, und das ist die
 * Entscheidung, auf die es hier ankommt: Die Spalte trägt weiter 2.5, weil
 * dort gerechnet wird und weil jeder andere Beleg sie so füllt. Der Bruch
 * steht im Text daneben – damit erscheint er auf dem Bildschirm, im PDF und im
 * Mailanhang, ohne dass eine einzige Zeile der gemeinsamen Belegdarstellung
 * angefasst werden müsste.
 */
export function mengenzusatz(menge: number): string {
  return `${alsBruch(menge)} ${menge === 1 ? 'Tag' : 'Tage'} pro Woche`
}

/**
 * Die vereinbarte Aufteilung einer Menge – «¾ + ½».
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Wofür
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Eine Familie vereinbart ¾ und ½ eines ganzen Betreuungstags. Verrechnet
 * wird die Summe – 1¼ –, und das ist richtig: Es ist EINE Leistung zu EINEM
 * Preis, und zwei Positionen über dieselbe Betreuung würden anders gerundet.
 * Auf der Rechnung stand damit aber nur noch 1¼, und die Mutter sucht ihre
 * Abmachung. Dieser Zusatz stellt sie daneben.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Was er ausdrücklich nicht tut
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Er rechnet nicht. Die Mengenspalte, der Einheitspreis, das Zeilennetto und
 * die Mehrwertsteuer bleiben, was sie ohne ihn wären – er ist Text.
 *
 * Und er erfindet nichts: Bei einem einzigen Teil (oder keinem) gibt er den
 * leeren Text zurück. Eine Aufteilung, die niemand vereinbart hat, darf auf
 * keiner Rechnung erscheinen, und «1¼ (= 1¼)» ist Lärm.
 */
export function aufteilungstext(teile: number[]): string {
  const brauchbar = teile.filter((t) => Number.isFinite(t) && t > 0)
  if (brauchbar.length < 2) return ''
  return brauchbar.map((t) => alsBruch(t)).join(' + ')
}

/* ═══════════════════════════════════════════════════════════════════════════
   Die Eingewöhnung
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Die Dauer der Eingewöhnung in Kalendertagen – zwei Wochen.
 *
 * Kalendertage und nicht Betreuungstage: Die Eingewöhnung läuft über zwei
 * Wochen am Stück, Wochenende eingeschlossen.
 *
 * Sie ist zugleich der VORSCHLAG und die OBERGRENZE: Die Pauschale von
 * CHF 500.00 gilt für höchstens vierzehn Tage. Kürzer darf ein Betrieb
 * vereinbaren – das Kind ist dann früher da, und die Pauschale bleibt
 * dieselbe –, länger nicht.
 */
export const EINGEWOEHNUNG_TAGE = 14

/**
 * Die Eingewöhnungspauschale in Rappen – CHF 500.00.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum dieser Betrag hier steht und nicht im Leistungskatalog
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Weil er keine Katalogzeile ist, die ein Betrieb pflegt, sondern eine feste
 * Regel der Kita-Betreuung – wie die vierzehn Tage selbst. Die erste Fassung
 * holte ihn über Code 1200 aus dem Katalog des Mandanten; das hiess: Wer ihn
 * dort nicht eintrug, bekam keine Position, und wer ihn mit der falschen
 * Einheit eintrug, bekam eine Fehlermeldung statt einer Rechnung. Ein
 * Pflichtschritt in einer Preisliste ist für eine feste Regel der falsche
 * Ort.
 *
 * Als Konstante und nicht als Zahl im Quelltext: Ändert sich die Pauschale,
 * ist genau eine Stelle anzufassen, und jede Prüfung rechnet von hier.
 */
export const EINGEWOEHNUNG_PAUSCHALE_RP = 50_000

/** Die Daten einer Eingewöhnung – alle als `YYYY-MM-DD`. */
export interface Eingewoehnungsfenster {
  /** Der erste Tag der Eingewöhnung. Er zählt mit. */
  beginn: string
  /** Der letzte Tag der Eingewöhnung. Er zählt mit. */
  ende: string
  /** Der erste Tag der regulären Betreuung – der Tag NACH dem Ende. */
  regulaerAb: string
  /** Die Dauer in Kalendertagen, beide Tage eingeschlossen. */
  tage: number
}

/**
 * Ein Datum um Kalendertage verschieben – `YYYY-MM-DD` hinein, dasselbe hinaus.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum hier Kalenderdaten und keine Stunden addiert werden
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `new Date(t.getTime() + 14 * 24 * 3600 * 1000)` ist der naheliegende Weg
 * und der falsche: In einer Zeitzone mit Sommerzeit ist ein Kalendertag
 * zweimal im Jahr nicht 24 Stunden lang, und dann verschiebt sich das Ende
 * um einen Tag. Gerechnet wird deshalb mit `Date.UTC` über die
 * Tageskomponenten – dort gibt es keine Sommerzeit, und der Überlauf über
 * Monats- und Jahresgrenzen erledigt die Normalisierung von `Date.UTC`
 * selbst: Der 20. Dezember plus 14 Tage ist der 3. Januar des Folgejahres.
 *
 * Diese Funktion liegt im gemeinsamen Paket, weil Oberfläche und Abrechnung
 * dieselben Daten nennen müssen. Zwei Rechnungen an zwei Orten sind zwei
 * Gelegenheiten, sich zu unterscheiden.
 */
export function tagPlus(datum: string, tage: number): string {
  const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datum)
  if (!treffer) throw new Error(`Kein gültiges Datum: ${datum}`)
  const [, j, m, t] = treffer
  const d = new Date(Date.UTC(Number(j), Number(m) - 1, Number(t) + tage))
  const zwei = (n: number): string => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${zwei(d.getUTCMonth() + 1)}-${zwei(d.getUTCDate())}`
}

/**
 * Wie viele Kalendertage liegen zwischen zwei Daten – beide eingeschlossen.
 *
 * Vom 25. November bis zum 8. Dezember sind es vierzehn: Beide Tage zählen
 * mit. `0` oder negativ heisst, dass das Ende vor dem Beginn liegt – das
 * prüft der Aufrufer und weist es ab.
 */
export function tageZwischen(von: string, bis: string): number {
  const ms = (d: string): number => {
    const treffer = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d)
    if (!treffer) throw new Error(`Kein gültiges Datum: ${d}`)
    const [, j, m, t] = treffer
    return Date.UTC(Number(j), Number(m) - 1, Number(t))
  }
  return Math.round((ms(bis) - ms(von)) / 86_400_000) + 1
}

/**
 * Der SPÄTESTE Tag einer Eingewöhnung, die an diesem Tag beginnt.
 *
 * Vierzehn Kalendertage, den ersten eingeschlossen – das Ende liegt deshalb
 * DREIZEHN Tage nach dem Beginn und nicht vierzehn, sonst wären es fünfzehn.
 *
 *   Beginn 25.11.2026  →  spätestens 08.12.2026
 *
 * Diese eine Funktion hat zwei Aufgaben, und das ist Absicht: Sie füllt das
 * Enddatum beim Ankreuzen vor UND sie ist die Obergrenze des Kalenderfeldes
 * (`max`). Vorschlag und Grenze fallen zusammen, weil die Pauschale für
 * höchstens vierzehn Tage gilt – zwei Zahlen dafür wären zwei Gelegenheiten,
 * sich zu unterscheiden.
 *
 * Kürzer bleibt frei wählbar: Das Feld ist bearbeitbar, und gerechnet wird
 * mit dem, was dort steht.
 */
export function eingewoehnungsendeVorschlag(beginn: string): string {
  return tagPlus(beginn, EINGEWOEHNUNG_TAGE - 1)
}

/**
 * Der Zeitraum einer Eingewöhnung aus den beiden gewählten Daten.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Zwei Daten hinein, vier Angaben hinaus
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Beginn und Ende kommen aus dem Vertrag – der Betrieb wählt sie frei im
 * Kalender. Daraus folgen der erste reguläre Tag (der Tag NACH dem Ende) und
 * die Dauer in Kalendertagen. Beides wird gerechnet und nirgends gespeichert:
 * Eine gespeicherte Ableitung veraltet, sobald jemand ein Datum korrigiert.
 *
 *   25.11.2026 bis 08.12.2026  →  regulär ab 09.12.2026, 14 Tage
 *   02.11.2026 bis 20.11.2026  →  regulär ab 21.11.2026, 19 Tage
 *
 * `null` heisst: kein vollständiger, gültiger Zeitraum – ein Datum fehlt,
 * oder das Ende liegt vor dem Beginn. Dann gibt es keine Eingewöhnung, und
 * die Abrechnung verhält sich wie ohne. Die verständliche Meldung dazu
 * entsteht beim Speichern, nicht hier.
 */
export function eingewoehnungszeitraum(
  von: string | null | undefined,
  bis: string | null | undefined
): Eingewoehnungsfenster | null {
  if (!von || !bis) return null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(von) || !/^\d{4}-\d{2}-\d{2}$/.test(bis)) return null
  if (bis < von) return null
  return {
    beginn: von,
    ende: bis,
    regulaerAb: tagPlus(bis, 1),
    tage: tageZwischen(von, bis)
  }
}
